import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useBusinessContext } from "@/providers/business-provider";
import { useLocation } from "wouter";

type PlanName = "ZentroSeed" | "ZentroSprout" | "ZentroScale";

type SubscriptionRequiredOptions = {
  minLevel?: PlanName;
  message?: string;
  redirectTo?: string;
};

export function useSubscriptionRequired({
  minLevel = "ZentroSeed",
  message = "This feature requires a subscription",
  redirectTo = "/subscription/plans",
}: SubscriptionRequiredOptions = {}) {
  const { activeBusiness } = useBusinessContext();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [hasRequiredSubscription, setHasRequiredSubscription] = useState(false);

  // Define the order of subscription plans for comparison
  const planLevels: Record<PlanName, number> = {
    ZentroSeed: 1,
    ZentroSprout: 2,
    ZentroScale: 3,
  };

  const { 
    data: subscription,
    isLoading,
    error
  } = useQuery({
    queryKey: ['/api/businesses', activeBusiness?.id, 'subscription'],
    queryFn: async () => {
      if (!activeBusiness?.id) return null;
      
      try {
        const response = await apiRequest('GET', `/api/businesses/${activeBusiness.id}/subscription`);
        if (!response.ok) {
          if (response.status === 404) {
            return null; // No subscription
          }
          throw new Error('Failed to fetch subscription');
        }
        return response.json();
      } catch (error) {
        console.error('Error fetching subscription:', error);
        return null;
      }
    },
    enabled: !!activeBusiness?.id,
  });

  useEffect(() => {
    // Skip check if still loading or no active business
    if (isLoading || !activeBusiness) return;

    // Check if there's an active subscription
    if (!subscription || !subscription.plan) {
      setHasRequiredSubscription(false);
      toast({
        title: "Subscription Required",
        description: message,
        variant: "destructive",
      });
      
      if (redirectTo) {
        navigate(redirectTo);
      }
      return;
    }

    // Compare plan levels
    const userPlanLevel = planLevels[subscription.plan.name as PlanName] || 0;
    const requiredPlanLevel = planLevels[minLevel] || 0;

    if (userPlanLevel < requiredPlanLevel) {
      setHasRequiredSubscription(false);
      toast({
        title: `${minLevel} Required`,
        description: message,
        variant: "destructive",
      });
      
      if (redirectTo) {
        navigate(redirectTo);
      }
    } else {
      setHasRequiredSubscription(true);
    }
  }, [subscription, isLoading, activeBusiness, minLevel, message, toast, navigate, redirectTo]);

  return {
    hasRequiredSubscription,
    isLoading,
    error,
    subscription
  };
}